<template>
  <!-- Embed mode: minimal chrome for iframe -->
  <div v-if="isEmbedded" id="enrollment-flow-layout" class="tw-flex tw-flex-col embed-layout">
    <!-- Step Indicator -->
    <div class="tw-px-4 tw-flex-shrink-0">
      <div class="tw-max-w-2xl tw-mx-auto">
        <EnrollmentFlowSteps />
      </div>
    </div>

    <!-- Main Content -->
    <main class="tw-flex-1 tw-px-4 tw-py-4">
      <router-view v-slot="{ Component }">
        <Transition name="embed-fade" mode="out-in">
          <component :is="Component" />
        </Transition>
      </router-view>
    </main>
  </div>

  <!-- Standalone mode: header band → main content → sticky footer summary band -->
  <div v-else id="enrollment-flow-layout" class="tw-min-h-screen tw-w-screen tw-overflow-x-clip tw-flex tw-flex-col">
    <EnrollmentHeader />
    <main class="tw-flex-1 tw-w-full tw-py-5 tw-px-4 md:tw-px-6">
      <div class="tw-w-full tw-max-w-[1280px] tw-mx-auto tw-pb-8">
        <RouterView />
      </div>
    </main>
    <footer
      v-show="!isComparingPlans"
      class="tw-sticky tw-bottom-0 tw-z-30 tw-w-full tw-border-t tw-border-border tw-bg-card"
    >
      <div class="tw-w-full tw-max-w-[1280px] tw-mx-auto tw-px-4 md:tw-px-6">
        <EnrollmentElectionsSummary :hide-cost-summary="!showElectionsSummary" />
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onErrorCaptured, provide, ref } from "vue"
import { useRoute } from "vue-router"
import { useEmbedMode } from "@common/composables/embed/useEmbedMode"
import { useEmbedMessaging } from "@common/composables/embed/useEmbedMessaging"
import { useUserStore } from "@/store/user-store"
import { useEnrollmentDependentsSync } from "@/composables/enrollment-dependents"
import EnrollmentHeader from "@/layouts/enrollment-new/components/EnrollmentHeader.vue"
import EnrollmentFlowSteps from "@/modules/enrollment-new/pages/enrollment-flow/layout/components/EnrollmentFlowSteps.vue"
import EnrollmentElectionsSummary from "@/modules/enrollment-new/pages/enrollment-flow/layout/components/EnrollmentElectionsSummary.vue"
import {
  enrollmentFooterControlKey,
  type EnrollmentFooterControl
} from "@/modules/enrollment-new/pages/enrollment-flow/layout/enrollment-footer-control"

const { isEmbedded } = useEmbedMode()
const { send } = useEmbedMessaging()

const route = useRoute()
// The welcome/background step covers identity + verification before any
// benefit elections exist; the cost summary has nothing to summarize there.
const showElectionsSummary = computed(() => route.name !== "enroll:background")

const isComparingPlans = ref(false)
provide<EnrollmentFooterControl>(enrollmentFooterControlKey, {
  setIsComparingPlans: (value: boolean) => {
    isComparingPlans.value = value
  }
})

const userStore = useUserStore()
useEnrollmentDependentsSync(userStore.memberId ?? "")

// Capture errors from child components when in embed mode
onErrorCaptured((err: unknown) => {
  if (isEmbedded.value) {
    const message = err instanceof Error ? err.message : typeof err === "string" ? err : "Unknown error"
    send("ERROR", {
      code: "ENROLLMENT_RENDER_ERROR",
      message
    })
    return false // Suppress in embed mode - parent handles via postMessage
  }
  // Standalone: allow normal Vue error propagation (console, error boundaries)
})
</script>

<style scoped>
/* Embed mode styles */
.embed-layout {
  min-height: 100vh;
  min-height: 100dvh; /* Dynamic viewport height for mobile */
}

/* Smooth page transitions for embed mode */
.embed-fade-enter-active,
.embed-fade-leave-active {
  transition: opacity 0.15s ease;
}

.embed-fade-enter-from,
.embed-fade-leave-to {
  opacity: 0;
}
</style>
